import { CartAddEvent } from '@theme/events';

/**
 * Cart recommendations
 *
 * Shows a small carousel of recommended products inside the cart, based on the
 * products that are currently in it. It uses the same Shopify recommendation
 * engine as the "You may also like" section on the product page.
 *
 * - One request per cart product, results are cached for the page session.
 * - The available slots are shared between the cart products (round robin),
 *   so 6 slots and 2 cart products gives 3 recommendations each.
 * - Products already in the cart, sold out products and duplicates are skipped.
 * - Variants can be picked and added without leaving or reloading the cart.
 *
 * The cart re-renders itself after every change and resets this element's
 * children when it does. The element watches for that and repaints from memory,
 * so nothing flickers and the chosen variants are kept.
 */

/** How many recommendations to request per cart product (Shopify maximum is 10). */
const FETCH_LIMIT = 10;

/** @type {Map<string, Promise<any[]>>} */
const recommendationCache = new Map();

/** Variant chosen per product, kept across cart re-renders. @type {Map<number, number>} */
const chosenVariants = new Map();

const ARROW_ICON =
  '<svg width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M1 1l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] ?? char)
  );
}

/**
 * Formats an amount in cents with the shop's money format, e.g. "${{amount}}".
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const value = (Number(cents) || 0) / 100;

  /**
   * @param {number} decimals
   * @param {string} thousands
   * @param {string} decimal
   */
  const number = (decimals, thousands, decimal) => {
    const [whole = '0', fraction] = value.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
    switch (token) {
      case 'amount_no_decimals':
        return number(0, ',', '.');
      case 'amount_with_comma_separator':
        return number(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return number(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return number(2, "'", '.');
      case 'amount_no_decimals_with_space_separator':
        return number(0, ' ', ',');
      case 'amount_with_space_separator':
        return number(2, ' ', ',');
      case 'amount_with_period_and_space_separator':
        return number(2, ' ', '.');
      default:
        return number(2, ',', '.');
    }
  });
}

/**
 * @param {string | undefined | null} src
 * @param {number} width
 * @returns {string}
 */
function sizedImage(src, width) {
  if (!src) return '';
  return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
}

/**
 * @param {string} url
 * @returns {Promise<any[]>}
 */
function fetchRecommendations(url) {
  let request = recommendationCache.get(url);

  if (!request) {
    request = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((data) => (Array.isArray(data?.products) ? data.products : []))
      .catch(() => {
        // Do not keep a failed request, so it can be retried on the next cart change.
        recommendationCache.delete(url);
        return [];
      });
    recommendationCache.set(url, request);
  }

  return request;
}

/**
 * Shares the available slots between the cart products, one at a time.
 * @param {any[][]} lists - Recommendations per cart product, in cart order.
 * @param {number[]} cartProductIds
 * @param {number} limit
 * @returns {any[]}
 */
function pickProducts(lists, cartProductIds, limit) {
  const taken = new Set(cartProductIds);
  const queues = lists.map((list) => list.filter((product) => product?.available && product.variants?.length));
  const picks = [];
  let progressed = true;

  while (picks.length < limit && progressed) {
    progressed = false;

    for (const queue of queues) {
      if (picks.length >= limit) break;

      let next = queue.shift();
      while (next && taken.has(next.id)) next = queue.shift();
      if (!next) continue;

      taken.add(next.id);
      picks.push(next);
      progressed = true;
    }
  }

  return picks;
}

/**
 * @param {any} product
 * @returns {any[]}
 */
function availableVariants(product) {
  const variants = (product.variants ?? []).filter((/** @type {any} */ variant) => variant.available);
  return variants.length ? variants : product.variants ?? [];
}

/**
 * @param {any} product
 * @returns {any}
 */
function currentVariant(product) {
  const variants = availableVariants(product);
  const chosen = chosenVariants.get(product.id);
  return variants.find((/** @type {any} */ variant) => variant.id === chosen) ?? variants[0];
}

class CartRecommendations extends HTMLElement {
  /** @type {any[]} */
  #products = [];
  #ready = false;
  #visible = false;
  #loadedKey = '';
  #requestId = 0;
  #index = 0;

  /** Product ids on the server rendered attribute changed, the cart was updated. */
  #cartObserver = new MutationObserver(() => this.#refresh());

  /** Something else changed our children (the cart re-render), paint them again. */
  #contentObserver = new MutationObserver(() => this.#render());

  #intersectionObserver = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this.#intersectionObserver.disconnect();
    this.#visible = true;
    this.#refresh();
  });

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });

    this.#cartObserver.observe(this, { attributes: true, attributeFilter: ['data-product-ids'] });
    this.#contentObserver.observe(this, { childList: true, subtree: true });

    // Wait until the cart is actually on screen (drawer opened) before requesting anything.
    this.#intersectionObserver.observe(this);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('scroll', this.#onScroll, { capture: true });

    this.#cartObserver.disconnect();
    this.#contentObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  /** @returns {number[]} */
  get #cartProductIds() {
    return (this.dataset.productIds ?? '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  get #limit() {
    return Math.max(1, Number(this.dataset.limit) || 6);
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  /**
   * Runs our own DOM changes without the content observer reacting to them.
   * @param {() => void} change
   */
  #write(change) {
    change();
    this.#contentObserver.takeRecords();
  }

  async #refresh() {
    if (!this.#visible) return;

    const cartProductIds = this.#cartProductIds;
    const key = cartProductIds.join(',');
    if (key === this.#loadedKey) return;

    this.#loadedKey = key;
    const requestId = ++this.#requestId;

    // Whatever was just added to the cart should not be suggested any longer.
    this.#products = this.#products.filter((product) => !cartProductIds.includes(product.id));
    if (!this.#products.length) this.#ready = false;
    this.#render();

    if (!cartProductIds.length) {
      this.#ready = true;
      this.#render();
      return;
    }

    const limit = this.#limit;
    const baseUrl = this.dataset.url || '/recommendations/products';
    const intent = this.dataset.intent || 'related';

    // With more cart products than slots, the most recently added ones get a slot each.
    const lists = await Promise.all(
      cartProductIds
        .slice(0, limit)
        .map((id) =>
          fetchRecommendations(`${baseUrl}.json?product_id=${id}&limit=${FETCH_LIMIT}&intent=${encodeURIComponent(intent)}`)
        )
    );

    // The cart changed again while we were waiting.
    if (requestId !== this.#requestId) return;

    this.#products = pickProducts(lists, cartProductIds, limit);
    this.#ready = true;
    this.#render();
  }

  #render() {
    this.#write(() => {
      if (!this.#cartProductIds.length || (this.#ready && !this.#products.length)) {
        // No children at all: the element hides itself with :empty.
        this.replaceChildren();
        return;
      }

      const heading = `<h3 class="cart-recs__heading">${escapeHtml(this.dataset.heading)}</h3>`;

      if (!this.#ready) {
        this.innerHTML = `<div class="cart-recs" aria-busy="true">${heading}<div class="cart-recs__skeleton"></div></div>`;
        return;
      }

      const hasSeveral = this.#products.length > 1;
      const arrow = (/** @type {'prev' | 'next'} */ direction) =>
        hasSeveral
          ? `<button type="button" class="cart-recs__arrow cart-recs__arrow--${direction} button-unstyled" data-direction="${direction}" aria-label="${
              direction === 'prev' ? 'Previous product' : 'Next product'
            }">${ARROW_ICON}</button>`
          : '';

      this.innerHTML = `
        <div class="cart-recs">
          ${heading}
          <div class="cart-recs__carousel">
            ${arrow('prev')}
            <ul class="cart-recs__track" role="list">
              ${this.#products.map((product) => this.#cardTemplate(product)).join('')}
            </ul>
            ${arrow('next')}
          </div>
        </div>`;

      // Stay on the product the shopper was looking at.
      this.#index = Math.min(this.#index, this.#products.length - 1);
      const track = this.querySelector('.cart-recs__track');
      if (track instanceof HTMLElement && track.clientWidth) {
        track.scrollLeft = this.#index * track.clientWidth;
      }
    });
  }

  /**
   * @param {any} product
   * @returns {string}
   */
  #cardTemplate(product) {
    const variants = availableVariants(product);
    const variant = currentVariant(product);
    const url = escapeHtml(product.url || `/products/${product.handle}`);
    const title = escapeHtml(product.title);
    const hasOnlyDefaultVariant = product.variants.length === 1 && product.variants[0].title === 'Default Title';

    const select = hasOnlyDefaultVariant
      ? ''
      : `<select class="cart-recs__select" aria-label="${title}">
          ${variants
            .map(
              (/** @type {any} */ option) =>
                `<option value="${option.id}"${option.id === variant.id ? ' selected' : ''}>${escapeHtml(
                  option.title
                )}</option>`
            )
            .join('')}
        </select>`;

    return `
      <li class="cart-recs__card" data-product-id="${product.id}">
        <a class="cart-recs__media" href="${url}" tabindex="-1" aria-hidden="true">${this.#imageTemplate(
      product,
      variant
    )}</a>
        <div class="cart-recs__body">
          <a class="cart-recs__title" href="${url}">${title}</a>
          <div class="cart-recs__price">${this.#priceTemplate(variant)}</div>
          <div class="cart-recs__actions">
            ${select}
            <button type="button" class="button cart-recs__add">${escapeHtml(this.dataset.addLabel || 'Add')}</button>
          </div>
          <p class="cart-recs__error" role="alert" hidden></p>
        </div>
      </li>`;
  }

  /**
   * @param {any} product
   * @param {any} variant
   * @returns {string}
   */
  #imageTemplate(product, variant) {
    const src = variant?.featured_image?.src || product.featured_image || product.images?.[0];
    if (!src) return '';

    return `<img class="cart-recs__image" src="${escapeHtml(sizedImage(src, 180))}" alt="" width="72" height="72" loading="lazy">`;
  }

  /**
   * @param {any} variant
   * @returns {string}
   */
  #priceTemplate(variant) {
    const format = this.#moneyFormat;
    const price = `<span class="cart-recs__price-current">${escapeHtml(formatMoney(variant.price, format))}</span>`;

    if (variant.compare_at_price > variant.price) {
      return `<s class="cart-recs__price-compare">${escapeHtml(formatMoney(variant.compare_at_price, format))}</s>${price}`;
    }

    return price;
  }

  /**
   * @param {Element | null} card
   * @returns {any}
   */
  #productFor(card) {
    const id = Number(card instanceof HTMLElement ? card.dataset.productId : 0);
    return this.#products.find((product) => product.id === id);
  }

  /** @param {Event} event */
  #onScroll = (event) => {
    const track = event.target;
    if (!(track instanceof HTMLElement) || !track.classList.contains('cart-recs__track') || !track.clientWidth) return;

    this.#index = Math.round(track.scrollLeft / track.clientWidth);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.classList.contains('cart-recs__select')) return;

    const card = select.closest('.cart-recs__card');
    const product = this.#productFor(card);
    if (!card || !product) return;

    chosenVariants.set(product.id, Number(select.value));
    const variant = currentVariant(product);

    // Price and image follow the chosen variant.
    this.#write(() => {
      const price = card.querySelector('.cart-recs__price');
      const media = card.querySelector('.cart-recs__media');
      const error = card.querySelector('.cart-recs__error');

      if (price) price.innerHTML = this.#priceTemplate(variant);
      if (media) media.innerHTML = this.#imageTemplate(product, variant);
      if (error instanceof HTMLElement) error.hidden = true;
    });
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;

    const arrow = target?.closest('.cart-recs__arrow');
    if (arrow instanceof HTMLElement) {
      this.#slide(arrow.dataset.direction === 'prev' ? -1 : 1);
      return;
    }

    const button = target?.closest('.cart-recs__add');
    if (button instanceof HTMLButtonElement) this.#add(button);
  };

  /** @param {number} step */
  #slide(step) {
    const track = this.querySelector('.cart-recs__track');
    const count = this.#products.length;
    if (!(track instanceof HTMLElement) || !track.clientWidth || count < 2) return;

    const current = Math.round(track.scrollLeft / track.clientWidth);
    const next = (current + step + count) % count;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#index = next;
    track.scrollTo({ left: next * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const card = button.closest('.cart-recs__card');
    const product = this.#productFor(card);
    if (!card || !product || button.disabled) return;

    const variantId = currentVariant(product).id;

    this.#write(() => {
      button.disabled = true;
      card.classList.add('cart-recs__card--adding');
    });

    // Ask for the cart sections in the same request, so the cart can update without a second round trip.
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
          .filter(Boolean)
      ),
    ];

    /** @type {Record<string, unknown>} */
    const body = { items: [{ id: variantId, quantity: 1 }] };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(`${this.dataset.addUrl || '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(typeof data.description === 'string' ? data.description : data.message);
      }

      // Take it out of the list straight away, the cart update below brings in a replacement.
      this.#products = this.#products.filter((item) => item.id !== product.id);
      this.#render();

      // Same event the product form sends: updates the cart items, totals and the cart count.
      this.dispatchEvent(
        new CartAddEvent({}, 'cart-recommendations', {
          source: 'product-form-component',
          itemCount: 1,
          productId: String(product.id),
          sections: data.sections,
        })
      );
    } catch (error) {
      // The cart may have re-rendered in the meantime, only touch the card if it is still ours.
      if (!card.isConnected) return;

      this.#write(() => {
        button.disabled = false;
        card.classList.remove('cart-recs__card--adding');

        const message = card.querySelector('.cart-recs__error');
        if (message instanceof HTMLElement) {
          message.textContent =
            (error instanceof Error && error.message) || this.dataset.errorLabel || 'Could not add this product. Try again.';
          message.hidden = false;
        }
      });
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}